import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import KPICard from '../../components/Dashboard/KPICard/KPICard';
import { fetchApi } from '../../../helpers/fetch';
import './SimplifiedDashboard.scss';

const SimplifiedDashboard = () => {
    const [period, setPeriod] = useState('30days');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const formatCurrency = useCallback((amountInCents) => {
        return `\u20AC${(amountInCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, []);

    const parseTrend = useCallback((trendStr) => {
        if (!trendStr) return null;
        return parseFloat(trendStr.replace(/[+%]/g, ''));
    }, []);

    const formatPercent = useCallback((part, total) => {
        if (!total || total === 0) return null;
        return `${Math.round((part / total) * 100)}%`;
    }, []);

    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchApi(`/admin/dashboard?period=${period}`);
            if (response.success && response.data) {
                setData(response.data);
            } else {
                setError('Failed to load dashboard data');
            }
        } catch (err) {
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    const periodOptions = [
        { value: '7days', label: 'Last 7 days' },
        { value: '30days', label: 'Last 30 days' },
        { value: '90days', label: 'Last 90 days' },
        { value: '1year', label: 'Last year' },
    ];

    return (
        <div className="simplified-dashboard">
            <div className="simplified-dashboard__header">
                <div>
                    <h1>Dashboard</h1>
                    <p>Platform overview</p>
                </div>
                <div className="simplified-dashboard__period">
                    {periodOptions.map((opt) => (
                        <button
                            key={opt.value}
                            className={`period-btn ${period === opt.value ? 'active' : ''}`}
                            onClick={() => setPeriod(opt.value)}
                            disabled={loading}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {error && (
                <div className="simplified-dashboard__error">
                    {error}
                    <button onClick={fetchDashboard}>Retry</button>
                </div>
            )}

            {/* Users */}
            <section className="simplified-dashboard__section">
                <h2 className="simplified-dashboard__section-title">Users</h2>
                <div className="simplified-dashboard__grid">
                    <KPICard
                        title="Total Users"
                        value={data?.users?.total?.value?.toLocaleString() ?? '—'}
                        trend={parseTrend(data?.users?.total?.trend)}
                        trendLabel={data?.users?.total?.trendLabel}
                        subtitle={data?.users?.contributors ? `${data.users.contributors.value?.toLocaleString()} contributors` : undefined}
                        color="#FF3232"
                        loading={loading}
                    />
                </div>
            </section>

            {/* Creators */}
            <section className="simplified-dashboard__section">
                <h2 className="simplified-dashboard__section-title">Creators</h2>
                <div className="simplified-dashboard__grid">
                    <KPICard
                        title="Total Creators"
                        value={data?.creators?.total?.value?.toLocaleString() ?? '—'}
                        trend={parseTrend(data?.creators?.total?.trend)}
                        trendLabel={data?.creators?.total?.trendLabel}
                        color="#ff9800"
                        loading={loading}
                    />
                    <KPICard
                        title="Publishing Creators"
                        value={data?.creators?.publishing?.value?.toLocaleString() ?? '—'}
                        trend={parseTrend(data?.creators?.publishing?.trend)}
                        trendLabel={data?.creators?.publishing?.trendLabel}
                        subtitle={data?.creators?.total?.value ? `${formatPercent(data.creators.publishing?.value, data.creators.total.value)} of creators` : undefined}
                        color="#ff9800"
                        loading={loading}
                    />
                    <KPICard
                        title="Monetizing Creators"
                        value={data?.creators?.monetizing?.value?.toLocaleString() ?? '—'}
                        trend={parseTrend(data?.creators?.monetizing?.trend)}
                        trendLabel={data?.creators?.monetizing?.trendLabel}
                        subtitle={data?.creators?.publishing?.value ? `${formatPercent(data.creators.monetizing?.value, data.creators.publishing.value)} of publishing` : undefined}
                        color="#ff9800"
                        loading={loading}
                    />
                </div>
            </section>

            {/* Creations */}
            <section className="simplified-dashboard__section">
                <h2 className="simplified-dashboard__section-title">Creations</h2>
                <div className="simplified-dashboard__grid">
                    <KPICard
                        title="Total Creations"
                        value={data?.creations?.value?.toLocaleString() ?? '—'}
                        trend={parseTrend(data?.creations?.trend)}
                        trendLabel={data?.creations?.trendLabel}
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Total News"
                        value={data?.news?.value?.toLocaleString() ?? '—'}
                        trend={parseTrend(data?.news?.trend)}
                        trendLabel={data?.news?.trendLabel}
                        color="#4caf50"
                        loading={loading}
                    />
                </div>
            </section>

            {/* Financials */}
            <section className="simplified-dashboard__section">
                <h2 className="simplified-dashboard__section-title">Financials</h2>
                <div className="simplified-dashboard__grid">
                    <KPICard
                        title="GMV"
                        value={data?.gmv ? formatCurrency(data.gmv.value) : '—'}
                        trend={parseTrend(data?.gmv?.trend)}
                        trendLabel={data?.gmv?.trendLabel}
                        color="#9c27b0"
                        loading={loading}
                    />
                    <KPICard
                        title="Revenue"
                        value={data?.revenue?.value != null ? formatCurrency(data.revenue.value) : '—'}
                        trend={parseTrend(data?.revenue?.trend)}
                        trendLabel={data?.revenue?.trendLabel}
                        subtitle="10% platform commission"
                        color="#9c27b0"
                        loading={loading}
                    />
                </div>
            </section>

            {/* Quick Actions */}
            <div className="simplified-dashboard__actions">
                <h3>Quick Actions</h3>
                <div className="actions-grid">
                    <Link to="/admin/invite" className="action-link">Invite Users</Link>
                    <Link to="/admin/users" className="action-link">Manage Users</Link>
                    <Link to="/admin/creation" className="action-link">Manage Creations</Link>
                    <Link to="/admin/paiements" className="action-link">Payments</Link>
                    <Link to="/admin/dashboards/overview" className="action-link">Full Dashboard</Link>
                </div>
            </div>
        </div>
    );
};

export default SimplifiedDashboard;
